import { LogicDeps } from '@chegg-wt/core/state'
import { AppState, FSACreatorPayload } from '@chegg-wt/core/state/types'
import { createLogic } from 'redux-logic'
import { openModal } from '@chegg-wt/core/state/modals/actions'
import { REDESIGNED_UNLOCK_MODAL } from '@chegg-wt/core/state/modals/constants'
import {
  SESSION_STORAGE_KEYS,
  removeSessionStorageItem,
  setSessionStorageItem,
} from '@chegg-wt/core/utils/sessionStorage'
import { transformDeprecatedStyleId } from '@chegg-wt/core/utils/styles'
import { getCitationsFromSessionStorage } from '@chegg-wt/core/utils/writingForms'
import {
  citationCreate,
  citationUpdate,
  citationValidated,
  citationUpdateSucceeded,
  citationCreateSucceeded,
} from '../citation/actions'
import { CitationSuccessPayload } from '../citation/types'
import { flowed } from '../app/actions'
import { isProSelector } from '../user/selectors'
import { formLoaded, formPageAuthComplete, writingFormSubmit } from './actions'
import {
  WRITING_FORM_SUBMIT_REQUESTED,
  FORM_SUBMIT_START,
  FORM_SUBMIT_STOP,
} from './constants'

import { FormPageAuthCompletePayload, FormSubmitPayload } from './types'

export const formLoadedLogic = createLogic<
  AppState,
  FSACreatorPayload<typeof formLoaded>,
  any,
  LogicDeps
>({
  type: [formLoaded],
  transform({ action }, next) {
    action.payload.style = transformDeprecatedStyleId(action.payload.style) // eslint-disable-line no-param-reassign
    next(action)
  },
})

export const setupFormDataSessionLogic = createLogic<AppState, undefined, any, LogicDeps>(
  {
    type: [FORM_SUBMIT_START],
    process() {
      // Removed legacy formData value, added true as string. Since it was only used for assertion.
      setSessionStorageItem(SESSION_STORAGE_KEYS.formData, 'true')
    },
  }
)

export const writingFormSubmitLogic = createLogic<
  AppState,
  FormSubmitPayload,
  any,
  LogicDeps
>({
  type: [WRITING_FORM_SUBMIT_REQUESTED],
  async process({ action }, dispatch, done) {
    const { initialCitationData, citation, redirect = true } = action.payload
    const { validatorStatus, ...restCitationData } = citation
    dispatch(citationValidated(citation.validatorStatus))

    const isNewCitation = !initialCitationData.citationId

    if (isNewCitation) {
      dispatch(citationCreate(restCitationData, citation.annotation, redirect))
    } else {
      dispatch(citationUpdate(initialCitationData, restCitationData, citation.annotation))
    }
    sessionStorage.removeItem(SESSION_STORAGE_KEYS.citationObject)
    done()
  },
})

export const teardownFormDataSessionLogic = createLogic<AppState, {}, any, LogicDeps>({
  type: [citationCreateSucceeded, citationUpdateSucceeded, FORM_SUBMIT_STOP],
  process() {
    removeSessionStorageItem(SESSION_STORAGE_KEYS.formData)
  },
})

export const teardownFormRouteSessionLogic = createLogic<AppState, {}, any, LogicDeps>({
  type: [citationCreateSucceeded, citationUpdateSucceeded],
  process() {
    removeSessionStorageItem(SESSION_STORAGE_KEYS.formRoute)
  },
})

export const successfulFormSubmitLogic = createLogic<
  AppState,
  CitationSuccessPayload,
  any,
  LogicDeps
>({
  type: [citationCreateSucceeded, citationUpdateSucceeded],
  validate({ action }, allow, reject) {
    if (!action.payload.navigate) {
      reject(action)
    }
    allow(action)
  },
  process() {
    return flowed('toBibliography')
  },
})

export const formPageAuthCompleteLogic = createLogic<
  AppState,
  FormPageAuthCompletePayload,
  any,
  LogicDeps
>({
  type: [formPageAuthComplete],
  process({ action, getState }, dispatch, done) {
    const isPro = isProSelector(getState())

    const { alwaysParticipate } = action.payload

    if (isPro) {
      const writingFormsCitationData = getCitationsFromSessionStorage()
      const { citation, initialCitationData } = writingFormsCitationData
      dispatch(writingFormSubmit({ citation, initialCitationData }))
    } else {
      dispatch(
        openModal({
          name: REDESIGNED_UNLOCK_MODAL,
          data: { withAuthBanner: false, alwaysParticipate },
        })
      )
    }
    done()
  },
})
